import {
  type RosterItemResponseOrganizationRosterResponseRelatedPagedData,
  type SportFilter,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { getHighSchoolSportLabel } from '@on3/ui-lib/utils/url';
import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb';
import { HighSchoolRoster } from 'components/HighSchool/Roster/HighSchoolRoster';
import { AdSidebarLayout } from 'components/Layouts/AdSidebarLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { AdminLink } from 'components/User/AdminLink';
import { usePathname } from 'next/navigation';
import { createCanonicalUrl, createDescription, createTitle } from 'utils/meta';

export interface IHighSchoolPageProps extends IGenericPageProps {
  highSchoolRoster?: RosterItemResponseOrganizationRosterResponseRelatedPagedData;
  filters: {
    sports?: SportFilter[];
    years?: number[];
  };
}
export const HighSchoolRosterPage = (props: IHighSchoolPageProps) => {
  const { highSchoolRoster, siteData } = props || {};
  const { relatedModel, pagination } = highSchoolRoster || {};
  const { year, sport, orgResponse } = relatedModel || {};
  const highSchoolName = orgResponse?.name || 'High School';

  const highSchoolUrl = `/high-school/${orgResponse?.urlSlug}/`;
  const primaryColor = adjustColorForReadability(
    orgResponse?.primaryColor || '#fe3b1f',
  );
  const noIndex = (pagination?.count ?? 0) === 0;
  const { defaultYears } = siteData || {};
  const sportLabel = sport ? getHighSchoolSportLabel(sport) : '';
  const isCurrentYear = year === defaultYears?.footballSeasonYear;

  const description = createDescription(
    'Discover the current {0} recruits for {1}. Explore detailed profiles of the {1} recruits at the school in {2}. Stay updated with On3.',
    sportLabel,
    highSchoolName,
    year,
  );
  const title = createTitle(
    highSchoolName,
    !isCurrentYear ? year : '',
    sportLabel,
    'Roster',
  );
  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <AdSidebarLayout>
        <AdminLink
          path="/organization/[id]/roster/"
          query={{
            year: year?.toString(),
            sport: slugify(sport?.name),
          }}
          title="Edit Roster"
        />

        <BreadCrumb
          color={primaryColor}
          crumbs={[
            { title: 'High School', href: '/high-school/' },
            { title: highSchoolName, href: highSchoolUrl },
          ]}
          title={title}
        />

        <HighSchoolRoster
          filters={props.filters}
          highSchoolRoster={highSchoolRoster}
          key={`${year}-${sport?.key}`}
          title={title}
        />
      </AdSidebarLayout>
    </PageWrapper>
  );
};
